import type { AccountAddressesSuccessResponse, Profile } from '@libs/common-next';

import type { ProfileCompletionStepId } from './constants';
import { PROFILE_COMPLETION_STEP_ORDER, TOTAL_PROFILE_COMPLETION_STEPS } from './constants';

export type ProfileCompletionStepState = {
    id: ProfileCompletionStepId;
    isComplete: boolean;
};

export type DerivedProfileCompletion = {
    steps: ProfileCompletionStepState[];
    completedCount: number;
    totalCount: number;
    firstIncompleteStep: ProfileCompletionStepId | null;
    isProfileComplete: boolean;
};

export type ProfileCompletionSessionFlags = {
    noPetSelected?: boolean;
    notificationsSubmitted?: boolean;
};

function hasRequiredAddressFields(address: {
    streetLine1?: string | null;
    city?: string | null;
    zipPostalCode?: string | null;
}): boolean {
    return Boolean(address.streetLine1?.trim() && address.city?.trim() && address.zipPostalCode?.trim());
}

export function hasCompletedAddressStep(addresses: AccountAddressesSuccessResponse[] | null | undefined): boolean {
    if (!Array.isArray(addresses)) {
        return false;
    }

    return addresses.some(hasRequiredAddressFields);
}

export function hasCompletedNotificationsStep(
    profile: Profile | null | undefined,
    { notificationsSubmitted = false }: ProfileCompletionSessionFlags = {}
): boolean {
    if (notificationsSubmitted) {
        return true;
    }

    if (!profile) {
        return false;
    }

    return !profile.isEmailOptedOut && !profile.isTextOptedOut;
}

export function hasCompletedPetStep(
    petCount: number,
    { noPetSelected = false }: ProfileCompletionSessionFlags = {}
): boolean {
    if (petCount > 0) {
        return true;
    }

    return noPetSelected;
}

export function deriveProfileCompletionSteps({
    profile,
    addresses,
    petCount,
    sessionFlags = {},
}: {
    profile: Profile | null | undefined;
    addresses: AccountAddressesSuccessResponse[] | null | undefined;
    petCount: number;
    sessionFlags?: ProfileCompletionSessionFlags;
}): DerivedProfileCompletion {
    const addressComplete = hasCompletedAddressStep(addresses);
    const notificationsComplete = hasCompletedNotificationsStep(profile, sessionFlags);

    const completionByStep: Record<ProfileCompletionStepId, boolean> = {
        pet: hasCompletedPetStep(petCount, sessionFlags),
        address: addressComplete,
        notifications: notificationsComplete,
    };

    const steps = PROFILE_COMPLETION_STEP_ORDER.map(id => ({
        id,
        isComplete: completionByStep[id],
    }));

    const completedCount = steps.filter(step => step.isComplete).length;
    const firstIncompleteStep = steps.find(step => !step.isComplete)?.id ?? null;

    return {
        steps,
        completedCount,
        totalCount: TOTAL_PROFILE_COMPLETION_STEPS,
        firstIncompleteStep,
        isProfileComplete: profile?.isProfileComplete === true,
    };
}
